<!-- Adapted from YesPlayMusic v0.4.11 src/views/settings.vue (MIT, Copyright (c) 2020-2023 qier222). Original settings grouping and SCSS retained where usable; unavailable Electron/account controls are omitted. -->
<template>
  <div v-if="workspace.appearance === 'salt'" class="salt-settings-page" :data-settings-page="sectionId || 'home'">
    <header class="salt-settings-header"><router-link v-if="sectionId" class="settings-back" :to="sectionLocation()" aria-label="返回设置">返回设置</router-link><h1 ref="settingsTitle" tabindex="-1">{{ sectionTitle }}</h1></header>
    <template v-if="!sectionId">
      <SaltCard header="偏好设置" inset>
        <router-link v-for="section in sections" :key="section.id" class="salt-settings-link" :to="sectionLocation(section.id)" :data-settings-section="section.id" :aria-label="section.title">
          <SaltItem class="salt-setting-row salt-settings-category" :title="section.title" :description="section.description" arrow="right"><template #trailing><span class="settings-summary">{{ sectionSummary(section.id) }}</span></template></SaltItem>
        </router-link>
      </SaltCard>
      <SaltCard header="账号与连接" inset><router-link class="salt-settings-link" to="/account"><SaltItem class="salt-setting-row salt-settings-category" title="账号与登录" description="云端账号、QQ音乐与网易云音乐" arrow="right" /></router-link></SaltCard>
    </template>
    <SaltCard v-if="sectionId === 'appearance'" header="界面与动态效果" inset>
      <SaltItem v-if="!saltOnly" class="salt-setting-row" title="界面布局"><template #trailing><SaltSelect aria-label="界面布局" :value="workspace.appearance" @change="setLayoutValue"><option value="salt">Salt · 三工作空间</option><option value="yesplaymusic">YesPlayMusic · 原版布局</option><option value="apple">Apple · 布局</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row salt-setting-toggle" title="显示工作空间切换" description="在侧栏显示或隐藏“综合 / 在线 / 本地”切换。"><template #trailing><SaltSwitch aria-label="显示工作空间切换" :value="workspace.showWorkspaceSwitcher" @change="setWorkspaceSwitcherValue" /></template></SaltItem>
      <SaltItem class="salt-setting-row" title="默认工作空间" description="下次打开时进入所选工作空间。"><template #trailing><SaltSelect aria-label="默认工作空间" :value="workspace.defaultMode" @change="setDefaultWorkspaceModeValue"><option value="all">综合</option><option value="online">在线</option><option value="local">本地</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="外观"><template #trailing><SaltSelect aria-label="外观" :value="state.theme" @change="setThemeValue"><option value="system">跟随系统</option><option value="light">浅色</option><option value="dark">深色</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="界面材质"><template #trailing><SaltSelect aria-label="界面材质" :value="workspace.material" @change="setMaterial"><option value="acrylic">亚克力</option><option value="mica">云母</option><option value="none">纯色</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row salt-setting-toggle" title="显示搜索范围" description="在顶部搜索栏显示综合 / 本地 / 网络切换。"><template #trailing><SaltSwitch aria-label="显示搜索范围" :value="workspace.showSearchScope" @change="setSearchScopeValue" /></template></SaltItem>
      <SaltItem class="salt-setting-row" title="默认搜索范围"><template #trailing><SaltSelect aria-label="默认搜索范围" :value="workspace.defaultSearchSource" @change="setDefaultSearchSourceValue"><option value="all">综合</option><option value="local">本地</option><option value="network">网络</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="沉浸背景动态效果" description="遵循系统的“减少动态效果”设置。"><template #trailing><SaltSelect aria-label="沉浸背景动态效果" :value="state.motionLevel" @change="setMotionLevelValue"><option value="high">高</option><option value="medium">中</option><option value="low">低</option><option value="off">关闭</option></SaltSelect></template></SaltItem>
      <WallpaperSettings />
    </SaltCard>
    <SaltCard v-if="sectionId === 'playback'" header="播放控制" inset>
      <!-- SaltUI ItemSlider: label/value row followed by a full-width Slider. -->
      <div class="salt-setting-slider"><SaltItem class="salt-setting-row" title="音量"><template #trailing><output>{{ Math.round(state.volume * 100) }}%</output></template></SaltItem><SaltSlider aria-label="播放器音量" :value="state.volume" @input="setVolumeValue" /></div>
      <div class="salt-setting-slider"><SaltItem class="salt-setting-row" title="播放速度" description="立即应用到当前播放，保存在当前浏览器。"><template #trailing><div class="salt-slider-value"><output>{{ ui.playbackRate }}×</output><SaltButton size="small" style-type="plain" @click="resetPlaybackRate">重置</SaltButton></div></template></SaltItem><SaltSlider aria-label="播放速度" :min="0.5" :max="2" :step="0.05" :value="ui.playbackRate" @input="setPlaybackRateValue" /></div>
      <SaltItem class="salt-setting-row salt-setting-quality" title="默认在线音质"><template #trailing><div class="salt-quality-controls"><SaltSelect aria-label="默认音质平台" :value="qualityPlatform" @change="qualityPlatform = $event"><option value="tx">QQ音乐</option><option value="wy">网易云音乐</option></SaltSelect><SaltSelect aria-label="默认在线音质" :value="playback.defaultQualityByPlatform[qualityPlatform]" @change="setDefaultQualityValue"><option v-for="quality in qualityOptions" :key="quality" :value="quality">{{ qualityLabel(quality, qualityPlatform) }}</option></SaltSelect></div></template></SaltItem>
      <SaltItem class="salt-setting-row" title="在线取流方式" description="切换不打断当前歌曲；用于下一次播放和下载。"><template #trailing><SaltSelect aria-label="在线取流方式" :value="playback.transport" @change="setOnlineTransportValue"><option value="browser">浏览器直连</option><option value="relay">网站代理</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="音频设置" description="查看当前歌曲的音源、音质与音频参数。"><template #trailing><SaltButton style-type="subtle" @click="openAudioSettings">查看</SaltButton></template></SaltItem>
      <SaltItem class="salt-setting-row" title="通用控制条样式" description="MineRadio 为当前稳定样式；Apple 为候选样式。"><template #trailing><SaltSelect aria-label="通用控制条样式" :value="workspace.controlBarStyle" @change="setControlBarStyleValue"><option value="mineradio">MineRadio</option><option value="apple">Apple</option></SaltSelect></template></SaltItem>
      <EqualizerSettings />
    </SaltCard>
    <SaltCard v-if="sectionId === 'lyrics'" header="歌词同步与显示" inset>
      <div class="salt-setting-slider"><SaltItem class="salt-setting-row" title="歌词偏移" description="正值延后，负值提前。"><template #trailing><div class="salt-slider-value"><output>{{ state.lyricOffsetMs > 0 ? '+' : '' }}{{ state.lyricOffsetMs }} ms</output><SaltButton size="small" style-type="plain" @click="resetLyricOffset">重置</SaltButton></div></template></SaltItem><SaltSlider aria-label="歌词偏移毫秒" :min="-5000" :max="5000" :step="100" :value="state.lyricOffsetMs" @input="setLyricOffsetValue" /></div>
      <SaltItem class="salt-setting-row salt-setting-toggle" title="本地歌曲自动查找歌词" description="仅补充缺失歌词，不改写音频文件。"><template #trailing><SaltSwitch aria-label="本地歌曲自动查找歌词" :value="lyrics.localAutoLookup" @change="setLocalAutoLookupValue" /></template></SaltItem>
      <SaltItem class="salt-setting-row salt-setting-toggle" title="桌面歌词" description="打开歌词浮窗；支持的浏览器可在浮窗内置顶。"><template #trailing><SaltSwitch aria-label="桌面歌词" :value="floating.open" @change="setFloatingLyricsValue" /></template></SaltItem>
    </SaltCard>
    <SaltCard v-if="sectionId === 'lyrics'" header="播放页歌词" inset>
      <SaltItem v-for="option in lyricToggleOptions" :key="option.key" class="salt-setting-row salt-setting-toggle" :title="option.title" :description="option.description"><template #trailing><SaltSwitch :aria-label="option.title" :value="lyricDisplay[option.key]" @change="setLyricDisplayValue(option.key, $event)" /></template></SaltItem>
      <SaltItem class="salt-setting-row" title="歌词字号"><template #trailing><SaltSelect aria-label="歌词字号" :value="lyricDisplay.size" @change="setLyricDisplayValue('size', $event)"><option v-for="option in lyricSizes" :key="option.value" :value="option.value">{{ option.label }}</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="歌词字重"><template #trailing><SaltSelect aria-label="歌词字重" :value="lyricDisplay.weight" @change="setLyricDisplayValue('weight', $event)"><option v-for="option in lyricWeights" :key="option.value" :value="option.value">{{ option.label }}</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="歌词字体"><template #trailing><SaltSelect aria-label="歌词字体" :value="lyricDisplay.font" @change="setLyricDisplayValue('font', $event)"><option v-for="option in lyricFonts" :key="option.value" :value="option.value">{{ option.label }}</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="歌词字距"><template #trailing><SaltSelect aria-label="歌词字距" :value="lyricDisplay.spacing" @change="setLyricDisplayValue('spacing', $event)"><option v-for="option in lyricSpacings" :key="option.value" :value="option.value">{{ option.label }}</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="逐字淡入宽度" description="仅作用于逐字歌词。"><template #trailing><SaltSelect aria-label="逐字淡入宽度" :value="lyricDisplay.fadeWidth" @change="setLyricDisplayValue('fadeWidth', $event)"><option v-for="option in lyricFadeWidths" :key="option.value" :value="option.value">{{ option.label }}</option></SaltSelect></template></SaltItem>
      <SaltItem class="salt-setting-row" title="预览播放页" :description="state.currentId ? '查看当前歌曲的完整播放页。' : '先选择一首歌曲，即可预览当前设置。'"><template #trailing><SaltButton style-type="subtle" :disabled="!state.currentId" @click="previewLyrics">预览播放页</SaltButton></template></SaltItem>
      <SaltItem class="salt-setting-row" title="恢复默认显示" description="重置本组显示选项，保留歌词偏移和自动查词设置。"><template #trailing><SaltButton style-type="plain" @click="resetLyricDisplaySettings">重置显示设置</SaltButton></template></SaltItem>
    </SaltCard>
    <SaltCard v-if="sectionId === 'playback'" header="在线音源" inset>
      <SaltItem v-if="!onlineSources.length" class="salt-setting-row" title="音源尚未载入" description="检查网络连接后刷新页面。" />
      <SaltItem v-for="source in onlineSources" :key="source.id" class="salt-setting-row salt-setting-source" :title="source.name || source.id" :description="sourceCaption(source)">
        <template #trailing><div class="salt-source-actions"><SaltButton style-type="subtle" :disabled="Boolean(testingSources[source.id])" @click="testSource(source.id)">{{ testingSources[source.id] ? '测试中…' : '测试连接' }}</SaltButton><SaltSwitch :aria-label="'启用 ' + (source.name || source.id)" :value="source.enabled !== false" :disabled="Boolean(savingSource[source.id])" @change="setSourceValue(source.id, $event)" /></div></template>
        <span v-if="sourceResult[source.id]" class="salt-source-result" role="status">{{ sourceResult[source.id] }}</span>
      </SaltItem>
    </SaltCard>
    <SaltCard v-if="sectionId === 'library'" header="音乐库与账号" inset>
      <SaltItem class="salt-setting-row salt-setting-toggle" title="WebDAV 归入本地音乐" description="远程音频仍需联网。"><template #trailing><SaltSwitch aria-label="WebDAV 归入本地音乐" :value="workspace.webdavInLocal" @change="setDavScopeValue" /></template></SaltItem>
      <SaltItem class="salt-setting-row" title="账号与登录" clickable arrow="right" @click="openAccounts" />
    </SaltCard>
    <SaltCard v-if="sectionId === 'library'" header="WebDAV 连接" inset><div class="salt-dav-connections"><WebDavConnections /></div></SaltCard>
    <SaltCard v-if="sectionId === 'library'" header="数据管理" inset>
      <LibraryCloudSettings salt />
      <SaltItem class="salt-setting-row" title="备份音乐库" description="导出歌曲索引、收藏、歌单与偏好，不包含音频文件。"><template #trailing><SaltButton style-type="subtle" @click="exportBackup">导出 JSON</SaltButton></template></SaltItem>
      <SaltItem class="salt-setting-row" title="恢复备份" description="恢复元数据后，本地音频需重新导入或授权。"><template #trailing><SaltButton style-type="subtle" @click="$refs.backup.click()">选择备份</SaltButton><input ref="backup" type="file" hidden accept=".json,application/json" @change="restoreBackup" /></template></SaltItem>
      <SaltItem class="salt-setting-row" title="音频缓存" :description="cacheDescription"><template #trailing><SaltButton style-type="subtle" @click="clearAudioCache">清除缓存</SaltButton></template></SaltItem>
      <SaltItem class="salt-setting-row" title="缓存上限" description="超出上限时自动删除较久未播放的音频缓存；留空为不限。"><template #trailing><input v-model="cacheLimitInput" @input="cacheLimitDirty = true" type="number" min="0" step="1" aria-label="音频缓存上限 MiB" placeholder="不限" style="width: 90px" /> MiB <SaltButton style-type="subtle" :disabled="cacheSaving" @click="saveCacheLimit">保存</SaltButton></template></SaltItem>
    </SaltCard>
    <ShortcutSettingsPanel v-if="sectionId === 'shortcuts'" />
    <footer v-if="!sectionId" class="salt-settings-footer"><strong>Simple Music</strong><p>本地库、收藏与偏好保存在当前浏览器</p><p><span v-if="sourceAvailable"><a href="/source/index.html">当前组合源码</a></span></p></footer>
  </div>
  <div v-else class="settings-page" :data-settings-page="sectionId || 'home'"><div class="container">
    <header class="settings-header"><router-link v-if="sectionId" class="settings-back" :to="sectionLocation()" aria-label="返回设置">返回设置</router-link><h2 ref="settingsTitle" tabindex="-1">{{ sectionTitle }}</h2></header>
    <nav v-if="!sectionId" class="settings-categories" aria-label="设置分类">
      <router-link v-for="section in sections" :key="section.id" class="settings-category" :to="sectionLocation(section.id)" :data-settings-section="section.id" :aria-label="section.title"><span><strong>{{ section.title }}</strong><small>{{ section.description }}</small></span><span class="settings-summary">{{ sectionSummary(section.id) }}</span></router-link>
      <router-link class="settings-category" to="/account"><span><strong>账号与登录</strong><small>云端账号、QQ音乐与网易云音乐</small></span><span>管理账号</span></router-link>
    </nav>
    <template v-if="sectionId === 'appearance'">
    <div class="item"><div class="left"><div class="title">界面布局</div><div class="description">切换布局会保留当前播放、队列与歌单。</div></div><div class="right"><select aria-label="界面布局" :value="workspace.appearance" @change="setLayout"><option value="salt">Salt · 三工作空间</option><option value="yesplaymusic">YesPlayMusic · 原版布局</option><option value="apple">Apple · 布局</option></select></div></div>
    <div class="item"><div class="left"><div class="title">显示工作空间切换</div><div class="description">在侧栏显示或隐藏“综合 / 在线 / 本地”切换。</div></div><div class="right"><input aria-label="显示工作空间切换" type="checkbox" :checked="workspace.showWorkspaceSwitcher" @keydown.enter.stop.prevent="activateSwitch" @change="setWorkspaceSwitcher($event)" /></div></div>
    <div class="item"><div class="left"><div class="title">默认工作空间</div><div class="description">下次打开时进入所选工作空间。</div></div><div class="right"><select aria-label="默认工作空间" :value="workspace.defaultMode" @change="setDefaultWorkspaceMode($event)"><option value="all">综合</option><option value="online">在线</option><option value="local">本地</option></select></div></div>
    <div class="item"><div class="left"><div class="title">显示搜索范围</div><div class="description">在顶部搜索栏显示综合 / 本地 / 网络切换。</div></div><div class="right"><input aria-label="显示搜索范围" type="checkbox" :checked="workspace.showSearchScope" @keydown.enter.stop.prevent="activateSwitch" @change="setSearchScope($event)" /></div></div>
    <div class="item"><div class="left"><div class="title">默认搜索范围</div></div><div class="right"><select aria-label="默认搜索范围" :value="workspace.defaultSearchSource" @change="setDefaultSearchSource($event)"><option value="all">综合</option><option value="local">本地</option><option value="network">网络</option></select></div></div>
    <div class="item"><div class="left"><div class="title">外观</div></div><div class="right"><select aria-label="外观" :value="state.theme" @change="setTheme"><option value="system">跟随系统</option><option value="light">浅色</option><option value="dark">深色</option></select></div></div>
    <div class="item"><div class="left"><div class="title">沉浸背景动态效果</div><div class="description">遵循系统的“减少动态效果”设置。</div></div><div class="right"><select aria-label="沉浸背景动态效果" :value="state.motionLevel" @change="setMotionLevel"><option value="high">高</option><option value="medium">中</option><option value="low">低</option><option value="off">关闭</option></select></div></div>
    </template>
    <template v-if="sectionId === 'playback'">
    <div class="item"><div class="left"><div class="title">音量</div><div class="description">保存在当前浏览器。</div></div><div class="right range-control"><input type="range" aria-label="播放器音量" min="0" max="1" step="0.01" :value="state.volume" @input="setVolume" /><output>{{ Math.round(state.volume * 100) }}%</output></div></div>
    <div class="item"><div class="left"><div class="title">播放速度</div><div class="description">立即应用到当前播放，保存在当前浏览器。</div></div><div class="right range-control"><input type="range" aria-label="播放速度" min="0.5" max="2" step="0.05" :value="ui.playbackRate" @input="setPlaybackRate" /><output>{{ ui.playbackRate }}×</output><button type="button" @click="resetPlaybackRate">重置</button></div></div>
    <div class="item"><div class="left"><div class="title">默认在线音质</div><div class="description">用于新播放请求，保存在当前浏览器。</div></div><div class="right"><select v-model="qualityPlatform" aria-label="默认音质平台"><option value="tx">QQ音乐</option><option value="wy">网易云音乐</option></select><select aria-label="默认在线音质" :value="playback.defaultQualityByPlatform[qualityPlatform]" @change="setDefaultQuality"><option v-for="quality in qualityOptions" :key="quality" :value="quality">{{ qualityLabel(quality, qualityPlatform) }}</option></select></div></div>
    <div class="item"><div class="left"><div class="title">在线取流方式</div><div class="description">用于下一次播放和下载。浏览器直连在本机解析并从音频 CDN 取流，受本机网络和上游跨域策略影响；网站代理经过服务端。切换不打断当前歌曲。</div></div><div class="right"><select aria-label="在线取流方式" :value="playback.transport" @change="setOnlineTransport"><option value="browser">浏览器直连</option><option value="relay">网站代理</option></select></div></div>
    <div class="item"><div class="left"><div class="title">音频设置</div><div class="description">查看当前歌曲的音源、音质与音频参数。</div></div><div class="right"><button type="button" @click="openAudioSettings">查看</button></div></div>
    <div class="item"><div class="left"><div class="title">通用控制条样式</div><div class="description">MineRadio 为当前稳定样式；Apple 为候选样式。</div></div><div class="right"><select aria-label="通用控制条样式" :value="workspace.controlBarStyle" @change="setControlBarStyle($event)"><option value="mineradio">MineRadio</option><option value="apple">Apple</option></select></div></div>
    <h3>在线音源</h3><p class="section-note">歌曲搜索、歌词与音频是否可用由上游决定。仅播放上游允许提供的内容。</p><div v-if="!onlineSources.length" class="item"><div class="left"><div class="title">音源尚未载入</div><div class="description">检查网络连接后刷新页面。</div></div></div><div v-for="source in onlineSources" :key="source.id" class="item source-item"><div class="left"><div class="title">{{ source.name || source.id }}</div><div class="description">{{ source.description || source.message || source.detail || '提供歌曲搜索、歌词与标准音频解析' }}</div><div v-if="sourceResult[source.id]" class="description" role="status">{{ sourceResult[source.id] }}</div></div><div class="right source-actions"><button :disabled="Boolean(testingSources[source.id])" @click="testSource(source.id)">{{ testingSources[source.id] ? '测试中…' : '测试连接' }}</button><div class="toggle"><input :id="'source-' + source.id" type="checkbox" :aria-label="'启用 ' + (source.name || source.id)" :checked="source.enabled !== false" :disabled="Boolean(savingSource[source.id])" @keydown.enter.stop.prevent="activateSwitch" @change="setSource(source.id, $event)" /><label :for="'source-' + source.id"></label></div></div></div>
    </template>
    <template v-if="sectionId === 'lyrics'">
    <div class="item"><div class="left"><div class="title">歌词偏移</div><div class="description">正值延后显示，负值提前显示。</div></div><div class="right range-control"><input aria-label="歌词偏移毫秒" type="range" min="-5000" max="5000" step="100" :value="state.lyricOffsetMs" @input="setLyricOffset" /><output>{{ state.lyricOffsetMs > 0 ? '+' : '' }}{{ state.lyricOffsetMs }} ms</output><button @click="resetLyricOffset">重置</button></div></div><div class="item"><div class="left"><div class="title">本地歌曲自动查找歌词</div><div class="description">播放没有时间轴歌词的本地歌曲时，按歌曲名和艺术家从已启用音源查找；只保存到本地索引，不改写音频文件。</div></div><div class="right"><input aria-label="本地歌曲自动查找歌词" type="checkbox" :checked="lyrics.localAutoLookup" @keydown.enter.stop.prevent="activateSwitch" @change="setLocalAutoLookup($event)" /></div></div>
    <div class="item"><div class="left"><div class="title">桌面歌词</div><div class="description">打开歌词浮窗；支持的浏览器可在浮窗内置顶。</div></div><div class="right"><input aria-label="桌面歌词" type="checkbox" :checked="floating.open" @keydown.enter.stop.prevent="activateSwitch" @change="setFloatingLyricsValue($event.target.checked)" /></div></div>
    <h3>播放页歌词</h3>
    <div v-for="option in lyricToggleOptions" :key="option.key" class="item"><div class="left"><div class="title">{{ option.title }}</div><div v-if="option.description" class="description">{{ option.description }}</div></div><div class="right"><input type="checkbox" :aria-label="option.title" :checked="lyricDisplay[option.key]" @keydown.enter.stop.prevent="activateSwitch" @change="changeLyricDisplay(option.key, $event)" /></div></div>
    <div class="item"><div class="left"><div class="title">歌词字号</div></div><div class="right"><select aria-label="歌词字号" :value="lyricDisplay.size" @change="changeLyricDisplay('size', $event)"><option v-for="option in lyricSizes" :key="option.value" :value="option.value">{{ option.label }}</option></select></div></div>
    <div class="item"><div class="left"><div class="title">歌词字重</div></div><div class="right"><select aria-label="歌词字重" :value="lyricDisplay.weight" @change="changeLyricDisplay('weight', $event)"><option v-for="option in lyricWeights" :key="option.value" :value="option.value">{{ option.label }}</option></select></div></div>
    <div class="item"><div class="left"><div class="title">歌词字体</div></div><div class="right"><select aria-label="歌词字体" :value="lyricDisplay.font" @change="changeLyricDisplay('font', $event)"><option v-for="option in lyricFonts" :key="option.value" :value="option.value">{{ option.label }}</option></select></div></div>
    <div class="item"><div class="left"><div class="title">歌词字距</div></div><div class="right"><select aria-label="歌词字距" :value="lyricDisplay.spacing" @change="changeLyricDisplay('spacing', $event)"><option v-for="option in lyricSpacings" :key="option.value" :value="option.value">{{ option.label }}</option></select></div></div>
    <div class="item"><div class="left"><div class="title">逐字淡入宽度</div><div class="description">仅作用于逐字歌词。</div></div><div class="right"><select aria-label="逐字淡入宽度" :value="lyricDisplay.fadeWidth" @change="changeLyricDisplay('fadeWidth', $event)"><option v-for="option in lyricFadeWidths" :key="option.value" :value="option.value">{{ option.label }}</option></select></div></div>
    <div class="item"><div class="left"><div class="title">预览播放页</div><div class="description">{{ state.currentId ? '查看当前歌曲的完整播放页。' : '先选择一首歌曲，即可预览当前设置。' }}</div></div><div class="right"><button type="button" :disabled="!state.currentId" @click="previewLyrics">预览播放页</button></div></div>
    <div class="item"><div class="left"><div class="title">恢复默认显示</div><div class="description">保留歌词偏移和自动查词设置。</div></div><div class="right"><button type="button" @click="resetLyricDisplaySettings">重置显示设置</button></div></div>
    </template>
    <template v-if="sectionId === 'library'">
    <LibraryCloudSettings />
    <div class="item"><div class="left"><div class="title">WebDAV 归入本地音乐</div><div class="description">仅改变本地浏览和搜索范围，远程音频仍需要网络。网易云盘始终属于在线。</div></div><div class="right"><input aria-label="WebDAV 归入本地音乐" type="checkbox" :checked="workspace.webdavInLocal" @keydown.enter.stop.prevent="activateSwitch" @change="setDavScope" /></div></div>
    <div class="item"><div class="left"><div class="title">账号与登录</div></div><div class="right"><router-link class="plain-button" to="/account">管理账号</router-link></div></div>
    <WebDavConnections />
    <h3>数据管理</h3><div class="item"><div class="left"><div class="title">备份音乐库</div><div class="description">导出歌曲索引、收藏、歌单与偏好，不包含音频文件。</div></div><div class="right"><button @click="exportBackup">导出 JSON</button></div></div><div class="item"><div class="left"><div class="title">恢复备份</div><div class="description">恢复元数据后，本地音频需重新导入或授权。</div></div><div class="right"><button @click="$refs.backup.click()">选择备份</button><input ref="backup" type="file" hidden accept=".json,application/json" @change="restoreBackup" /></div></div><div class="item"><div class="left"><div class="title">音频缓存</div><div class="description">{{ cacheDescription }}</div></div><div class="right"><button @click="clearAudioCache">清除缓存</button></div></div><div class="item"><div class="left"><div class="title">缓存上限</div><div class="description">超出上限时自动删除较久未播放的音频缓存；留空为不限。</div></div><div class="right"><input v-model="cacheLimitInput" @input="cacheLimitDirty = true" type="number" min="0" step="1" aria-label="音频缓存上限 MiB" placeholder="不限" style="width: 90px" /> MiB <button :disabled="cacheSaving" @click="saveCacheLimit">保存</button></div></div>
    </template>
    <ShortcutSettingsPanel v-if="sectionId === 'shortcuts'" />
    <div v-if="!sectionId" class="footer"><div>Simple Music</div><p class="author"><span v-if="sourceAvailable"><a href="/source/index.html">当前组合源码</a></span></p><p class="version">本地库、收藏与偏好保存在当前浏览器</p></div>
  </div></div>
</template>
<script>
import { runtime } from '../bridge';
import WebDavConnections from '../components/WebDavConnections.vue';
import {workspace,setAppearance,setWebdavInLocal,setMaterial,setShowWorkspaceSwitcher,setDefaultWorkspaceMode,setShowSearchScope,setDefaultSearchSource,setControlBarStyle} from '../workspace';
import {saltOnly} from '../product-flavor';
import { settingsSections, findSettingsSection, settingsLocation } from '../settings-navigation';
import { floatingLyrics, toggleFloatingLyrics } from '../floating-lyrics';
import { lyricDisplay, lyricSizeOptions, setLyricDisplay, resetLyricDisplay } from '../lyric-display-settings';
import SaltButton from '../salt/ui/SaltButton.vue';
import SaltSelect from '../salt/ui/SaltSelect.vue';
import SaltSwitch from '../salt/ui/SaltSwitch.vue';
import SaltCard from '../salt/ui/SaltCard.vue';
import SaltItem from '../salt/ui/SaltItem.vue';
import SaltSlider from '../salt/ui/SaltSlider.vue';
import WallpaperSettings from '../salt/WallpaperSettings.vue';
import ShortcutSettingsPanel from '../components/ShortcutSettingsPanel.vue';
import EqualizerSettings from '../salt/EqualizerSettings.vue';
import LibraryCloudSettings from '../components/LibraryCloudSettings.vue';
export default {
  name: 'Settings', components: { WebDavConnections, SaltButton, SaltSelect, SaltSwitch, SaltCard, SaltItem, SaltSlider, WallpaperSettings, ShortcutSettingsPanel, EqualizerSettings, LibraryCloudSettings },
  data() { return { cacheLimitInput: '', cacheLimitDirty: false, cacheSaving: false, cache: runtime.audioCache, workspace, saltOnly, state: runtime.state, ui: runtime.ui, playback: runtime.onlinePlayback, lyrics: runtime.lyricSettings, qualityPlatform: runtime.catalogSettings.platform, sourceAvailable: import.meta.env.PROD, testingSources: {}, sourceResult: {}, savingSource: {}, shortcuts: [['播放 / 暂停', 'Space'], ['快退 / 快进', '← / →'], ['静音', 'M'], ['搜索', 'Ctrl / ⌘ K']] }; },
  computed: {
    cacheDescription() { const used = (this.cache.bytes / 1048576).toFixed(1); return `已占用 ${used} MiB。清理后歌曲索引、歌词和原始文件保留，刷新后可能需要重新选择原文件。${this.cache.error || ''}`; },
    sections() { return settingsSections; },
    sectionId() { return findSettingsSection(this.$route.query.section, this.workspace.appearance)?.id || ''; },
    sectionTitle() { return findSettingsSection(this.sectionId)?.title || '设置'; },
    floating() { return floatingLyrics; },
    lyricDisplay() { return lyricDisplay; },
    lyricSizes() { return lyricSizeOptions; },
    lyricWeights() {
      const options = [{ value: 0, label: '默认' }, { value: 400, label: '常规' }, { value: 500, label: '中等' }, { value: 600, label: '半粗' }, { value: 700, label: '粗体' }];
      return options.some(option => option.value === this.lyricDisplay.weight) ? options : [...options, { value: this.lyricDisplay.weight, label: `自定义字重 ${this.lyricDisplay.weight}` }];
    },
    lyricFonts() {
      const options = [{ value: '', label: '默认' }, { value: 'system-ui, sans-serif', label: '系统字体' }, { value: '"Microsoft YaHei", sans-serif', label: '微软雅黑' }, { value: 'serif', label: '衬线字体' }, { value: 'monospace', label: '等宽字体' }];
      return options.some(option => option.value === this.lyricDisplay.font) ? options : [...options, { value: this.lyricDisplay.font, label: '自定义字体' }];
    },
    lyricSpacings() {
      const options = [{ value: 'normal', label: '默认' }, { value: '-0.02em', label: '紧凑' }, { value: '0.03em', label: '稍宽' }, { value: '0.06em', label: '宽' }, { value: '0.1em', label: '很宽' }];
      return options.some(option => option.value === this.lyricDisplay.spacing) ? options : [...options, { value: this.lyricDisplay.spacing, label: '自定义字距' }];
    },
    lyricFadeWidths() {
      const options = [{ value: 0, label: '关闭' }, { value: 0.25, label: '0.25 字宽' }, { value: 0.5, label: '0.5 字宽 · 默认' }, { value: 0.75, label: '0.75 字宽' }, { value: 1, label: '1 字宽' }];
      return options.some(option => option.value === this.lyricDisplay.fadeWidth) ? options : [...options, { value: this.lyricDisplay.fadeWidth, label: `${this.lyricDisplay.fadeWidth} 字宽` }];
    },
    lyricToggleOptions() { return [
      { key: 'original', title: '显示原文' }, { key: 'translation', title: '显示翻译' }, { key: 'roman', title: '显示音译' },
      { key: 'blur', title: '非当前行模糊' }, { key: 'scale', title: '当前行缩放' }, { key: 'spring', title: '弹簧滚动' },
      { key: 'swap', title: '交换翻译与音译位置', description: '只交换顺序，原文、翻译与音译仍可独立显示。' },
    ]; },
    onlineSources() { return this.state.sources.filter(source => ['yuxi', 'yuningxi'].includes(source.id) || source.type === 'online' || source.sourceType === 'online'); },
    qualityOptions() { return runtime.defaultQualityOptions(this.qualityPlatform); },
  },
  watch: {
    '$route.query.section': { immediate: true, handler(value) { if (value !== undefined && !findSettingsSection(value, this.workspace.appearance)) this.$router.replace(this.sectionLocation()).catch(() => {}); } },
    sectionId(next, previous) {
      this.$nextTick(() => {
        const target = !next && previous ? this.$el?.querySelector(`[data-settings-section="${previous}"]`) : this.$refs.settingsTitle;
        target?.focus({ preventScroll: true });
      });
    },
  },
  async mounted() { await runtime.refreshAudioCache(); if (!this.cacheLimitDirty) this.cacheLimitInput = this.cache.limit === null ? '' : String(this.cache.limit / 1048576); },
  methods: {
    async saveCacheLimit() {
      const raw = String(this.cacheLimitInput).trim();
      const mb = Number(raw);
      if (raw && (!Number.isSafeInteger(mb) || mb < 0 || !Number.isSafeInteger(mb * 1048576))) { runtime.notify('请输入有效的缓存上限', 'error'); return; }
      this.cacheSaving = true;
      try { await runtime.setAudioCacheLimit(raw ? mb * 1048576 : null); runtime.notify('音频缓存上限已保存', 'success'); } catch (error) { runtime.notify(error.message || '保存缓存上限失败', 'error'); } finally { this.cacheSaving = false; }
    },
    sectionLocation(section) { return settingsLocation(section, this.$route.query); },
    sectionSummary(section) {
      if (section === 'appearance') return { system: '跟随系统', light: '浅色', dark: '深色' }[this.state.theme];
      if (section === 'playback') return `${Math.round(this.state.volume * 100)}% · ${this.ui.playbackRate}×`;
      if (section === 'lyrics') return `${this.state.lyricOffsetMs > 0 ? '+' : ''}${this.state.lyricOffsetMs} ms`;
      if (section === 'library') return this.workspace.webdavInLocal ? 'WebDAV 归入本地' : 'WebDAV 独立显示';
      return `${this.shortcuts.length} 项`;
    },
    setPlaybackRateValue(value) { runtime.setPlaybackRate(Number(value)); },
    setPlaybackRate(event) { runtime.setPlaybackRate(Number(event.target.value)); },
    resetPlaybackRate() { runtime.setPlaybackRate(1); },
    setFloatingLyricsValue(value) { if (Boolean(value) !== floatingLyrics.open) toggleFloatingLyrics(); },
    setSearchScopeValue: setShowSearchScope,
    setDefaultSearchSourceValue: setDefaultSearchSource,
    setControlBarStyleValue: setControlBarStyle,
    setSearchScope(event) { setShowSearchScope(event.target.checked); },
    setDefaultSearchSource(event) { setDefaultSearchSource(event.target.value); },
    setControlBarStyle(event) { setControlBarStyle(event.target.value); },
    activateSwitch(event) { if (!event.repeat) event.currentTarget.click(); },
    openAudioSettings() { runtime.ui.audioSettingsOpen = true; },
    setLyricDisplayValue(key, value) {
      const saved = setLyricDisplay(key, ['weight', 'fadeWidth'].includes(key) ? Number(value) : value);
      if (!saved) runtime.notify('歌词显示设置未保存，请检查浏览器存储空间后重试', 'error');
      return saved;
    },
    changeLyricDisplay(key, event) {
      const input = event.target;
      this.setLyricDisplayValue(key, input.type === 'checkbox' ? input.checked : input.value);
      if (input.type === 'checkbox') input.checked = this.lyricDisplay[key];
      else input.value = this.lyricDisplay[key];
    },
    resetLyricDisplaySettings() {
      const saved = resetLyricDisplay();
      runtime.notify(saved ? '歌词显示设置已重置' : '歌词显示设置未能重置，请检查浏览器存储空间后重试', saved ? 'success' : 'error');
    },
    previewLyrics() { if (this.state.currentId) runtime.ui.lyricsOpen = true; },
    setMaterial,
    setVolumeValue(value) {
      const volume = Math.max(0, Math.min(1, Number(value)));
      if (volume > 0 && runtime.audio.muted) runtime.toggleMute();
      runtime.setVolume(volume);
    },
    setLyricOffsetValue: runtime.setLyricOffset,
    setLayoutValue: setAppearance,
    setWorkspaceSwitcherValue(value) { setShowWorkspaceSwitcher(value); },
    setDefaultWorkspaceModeValue(value) { setDefaultWorkspaceMode(value); },
    setWorkspaceSwitcher(event) { setShowWorkspaceSwitcher(event.target.checked); },
    setDefaultWorkspaceMode(event) { setDefaultWorkspaceMode(event.target.value); },
    setDavScopeValue: setWebdavInLocal,
    setThemeValue: runtime.setTheme,
    setMotionLevelValue: runtime.setMotionLevel,
    setLocalAutoLookupValue: runtime.setLocalAutoLookup,
    setOnlineTransportValue: runtime.setOnlineTransport,
    setDefaultQualityValue(value) { runtime.setDefaultQuality(value, this.qualityPlatform); },
    openAccounts() { this.$router.push('/account').catch(() => {}); },
    async setSourceValue(id, enabled) {
      this.$set(this.savingSource, id, true);
      try { await runtime.setSourceEnabled(id, enabled); }
      catch (error) { runtime.notify(error.message || '音源设置失败', 'error'); }
      finally { this.$delete(this.savingSource, id); }
    },
    setLayout(event) { setAppearance(event.target.value); },
    setDavScope(event) { setWebdavInLocal(event.target.checked); },
    setTheme(event) { runtime.setTheme(event.target.value); },
    setVolume(event) { this.setVolumeValue(event.target.value); },
    setDefaultQuality(event) { runtime.setDefaultQuality(event.target.value, this.qualityPlatform); },
    setOnlineTransport(event) { runtime.setOnlineTransport(event.target.value); },
    qualityLabel: runtime.qualityLabel,
    sourceCaption(source) {
      const status = String(source.status || '').trim();
      if (/检测中|测试中|loading/i.test(status)) return '正在检测';
      if (/失败|不可用|error|fail/i.test(status)) return '连接失败，可重试';
      if (/停用|disabled/i.test(status)) return '已停用';
      if (/可用|正常|通过|available|ready/i.test(status)) return '可用';
      return '尚未检测';
    },
    setLyricOffset(event) { runtime.setLyricOffset(Number(event.target.value)); },
    resetLyricOffset() { runtime.setLyricOffset(0); },
    setMotionLevel(event) { runtime.setMotionLevel(event.target.value); },
    setLocalAutoLookup(event) { runtime.setLocalAutoLookup(event.target.checked); },
    async setSource(id, event) {
      const input = event.target;
      this.$set(this.savingSource, id, true);
      try { await runtime.setSourceEnabled(id, input.checked); }
      catch (error) { runtime.notify(error.message || '音源设置失败', 'error'); }
      finally {
        input.checked = this.state.sources.find(source => source.id === id)?.enabled !== false;
        this.$delete(this.savingSource, id);
      }
    },
    async testSource(id) { if (this.testingSources[id]) return; this.$set(this.testingSources, id, true); this.$set(this.sourceResult, id, '正在连接上游…'); try { const result = await runtime.testSource(id); this.$set(this.sourceResult, id, typeof result === 'string' ? result : result.message || result.detail || '测试完成，请查看音源状态'); } catch (error) { this.$set(this.sourceResult, id, error.message || '测试失败'); } finally { this.$delete(this.testingSources, id); } },
    exportBackup() { runtime.exportBackup(); },
    async restoreBackup(event) { const file = event.target.files && event.target.files[0]; event.target.value = ''; if (!file) return; try { await runtime.restoreBackup(file); } catch (error) { runtime.notify(error.message || '恢复失败', 'error'); } },
    async clearAudioCache() { if (!window.confirm('清除浏览器保存的音频缓存？歌曲索引和原始文件保留。')) return; try { await runtime.clearAudioCache(); } catch (error) { runtime.notify(error.message || '缓存清除失败', 'error'); } },
  },
};
</script>
<style lang="scss" scoped>
.container { max-width: 100%; }
.settings-header { display:flex; align-items:center; flex-wrap:wrap; gap:16px; margin-bottom:20px; }
.settings-header h2 { margin:0; }
.settings-back { display:inline-flex; flex:0 0 auto; align-items:center; justify-content:center; min-height:44px; padding:0 8px; color:inherit; font-size:14px; text-decoration:none; border-radius:4px; }
.settings-back:hover { background:var(--salt-hover-overlay,var(--color-secondary-bg)); }
.settings-back:focus-visible, .settings-category:focus-visible, .salt-settings-link:focus-visible { outline:2px solid var(--salt-accent,var(--color-primary)); outline-offset:-2px; }
.settings-categories { max-width:900px; }
.settings-category { display:flex; align-items:center; justify-content:space-between; gap:16px; min-height:72px; padding:12px 0; box-sizing:border-box; border-bottom:1px solid var(--color-secondary-bg); color:var(--color-text); text-decoration:none; }
.settings-category > span:first-child { flex:1; min-width:0; }
.settings-category strong { font-size:16px; font-weight:600; }
.settings-category small { display:block; margin-top:6px; opacity:.65; font-size:13px; line-height:1.5; }
.settings-summary { display:block; max-width:100%; overflow-wrap:anywhere; line-height:1.5; }
.settings-category > .settings-summary { flex:0 1 38%; text-align:end; font-size:13px; opacity:.65; }
.item { gap: 24px; }
.item .left { min-width: 0; }
.item .right { flex-shrink: 0; }
.range-control, .source-actions { display: flex; align-items: center; gap: 12px; }
.range-control input { width: 144px; }
.range-control output { min-width: 64px; text-align: right; color: var(--color-text); font-size: 14px; }
.section-note { color: var(--color-text); opacity: .68; font-size: 14px; line-height: 1.7; }
.source-item .description { max-width: 400px; overflow-wrap: anywhere; }
.dav-editor { margin: 16px 0 30px; padding: 20px; border-radius: 12px; background: var(--color-secondary-bg); color: var(--color-text); }
.dav-editor-heading, .dav-editor-actions { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.dav-editor-heading strong { font-size: 16px; }
.dav-editor-heading p { margin: 7px 0 0; opacity: .68; font-size: 13px; line-height: 1.6; }
.dav-fields { display: grid; grid-template-columns: 1fr 1.4fr .8fr; gap: 14px; margin-top: 20px; }
.dav-fields label { display: grid; gap: 8px; min-width: 0; font-size: 13px; font-weight: 600; }
.dav-fields input { width: 100%; min-width: 0; border: 1px solid transparent; border-radius: 8px; padding: 10px 12px; background: var(--color-body-bg); color: var(--color-text); font: inherit; }
.dav-fields input:focus { border-color: var(--color-primary); outline: none; }
.credential-replace { display: flex; align-items: center; gap: 8px; margin-top: 18px; font-size: 13px; }
.dav-editor-actions { justify-content: flex-end; margin-top: 18px; }
.dav-location { word-break: break-all; }
.dav-status { font-weight: 600; }
.dav-status.success { color: var(--color-primary); opacity: 1; }
.dav-status.error { color: #d45757; opacity: 1; }
.dav-status.working { color: var(--color-primary); opacity: .82; }
.saved-connection { align-items: flex-start; }
.saved-connection .source-actions { justify-content: flex-end; flex-wrap: wrap; max-width: 330px; }
.danger-button { color: #d45757; }
.settings-page button:disabled { opacity: .4; cursor: default; }
.toggle input:focus-visible + label { outline: 2px solid var(--color-primary); outline-offset: 3px; }
@media(max-width: 800px) { .settings-page { margin-top: 12px; } h2 { margin-top: 18px; } .item { align-items: flex-start; flex-wrap: wrap; gap: 12px; } .item .right { max-width: 100%; } .range-control { flex-wrap: wrap; } .footer { font-size: 14px; } .source-item .left { width: 100%; } .dav-editor { padding: 16px; } .dav-fields { grid-template-columns: 1fr; } .dav-editor-heading { flex-direction: column; gap: 8px; } .saved-connection .source-actions { justify-content: flex-start; } #shortcut-table .col { min-width: 130px; } }

.settings-page {
  display: flex;
  justify-content: center;
  margin-top: 32px;
}
.container {
  margin-top: 24px;
  width: 720px;
}
h2 {
  margin-top: 48px;
  font-size: 36px;
  color: var(--color-text);
}

h3 {
  margin-top: 48px;
  padding-bottom: 12px;
  font-size: 26px;
  color: var(--color-text);
  border-bottom: 1px solid rgba(128, 128, 128, 0.18);
}

.user {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--color-secondary-bg);
  color: var(--color-text);
  padding: 16px 20px;
  border-radius: 16px;
  margin-bottom: 48px;
  img.avatar {
    border-radius: 50%;
    height: 64px;
    width: 64px;
  }
  img.cvip {
    height: 13px;
    margin-right: 4px;
  }
  .left {
    display: flex;
    align-items: center;
    .info {
      margin-left: 24px;
    }
    .nickname {
      font-size: 20px;
      font-weight: 600;
      margin-bottom: 2px;
    }
    .extra-info {
      font-size: 13px;
      .text {
        opacity: 0.68;
      }
      .vip {
        display: flex;
        align-items: center;
      }
    }
  }
  .right {
    .svg-icon {
      height: 18px;
      width: 18px;
      margin-right: 4px;
    }
    button {
      display: flex;
      align-items: center;
      font-size: 18px;
      font-weight: 600;
      text-decoration: none;
      border-radius: 10px;
      padding: 8px 12px;
      opacity: 0.68;
      color: var(--color-text);
      transition: 0.2s;
      margin: {
        right: 12px;
        left: 12px;
      }
      &:hover {
        opacity: 1;
        background: #eaeffd;
        color: #335eea;
      }
      &:active {
        opacity: 1;
        transform: scale(0.92);
        transition: 0.2s;
      }
    }
  }
}

.item {
  margin: 24px 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--color-text);

  .title {
    font-size: 16px;
    font-weight: 500;
    opacity: 0.78;
  }

  .description {
    font-size: 14px;
    margin-top: 0.5em;
    opacity: 0.7;
  }
}

.settings-page select {
  min-width: 192px;
  max-width: 600px;
  font-weight: 600;
  border: none;
  padding: 8px 12px 8px 12px;
  border-radius: 8px;
  color: var(--color-text);
  background: var(--color-secondary-bg);
  appearance: none;
  &:focus {
    outline: none;
    color: var(--color-primary);
    background: var(--color-primary-bg);
  }
}

.settings-page button {
  color: var(--color-text);
  background: var(--color-secondary-bg);
  padding: 8px 12px 8px 12px;
  font-weight: 600;
  border-radius: 8px;
  transition: 0.2s;
  &:hover {
    transform: scale(1.06);
  }
  &:active {
    transform: scale(0.94);
  }
}

input.text-input.margin-right-0 {
  margin-right: 0;
}
input.text-input {
  background: var(--color-secondary-bg);
  border: none;
  margin-right: 22px;
  padding: 8px 12px 8px 12px;
  border-radius: 8px;
  color: var(--color-text);
  font-weight: 600;
  font-size: 16px;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
input[type='number'] {
  -moz-appearance: textfield;
}

#proxy-form,
#real-ip {
  display: flex;
  align-items: center;
}
#proxy-form.disabled,
#real-ip.disabled {
  opacity: 0.47;
  button:hover {
    transform: unset;
  }
}

#shortcut-table {
  font-size: 14px;
  /* border: 1px solid black; */
  user-select: none;
  color: var(--color-text);
  .row {
    display: flex;
  }
  .row.row-head {
    opacity: 0.58;
    font-size: 13px;
    font-weight: 500;
  }
  .col {
    min-width: 192px;
    padding: 8px;
    display: flex;
    align-items: center;
    /* border: 1px solid red; */
    &:first-of-type {
      padding-left: 0;
      min-width: 128px;
    }
  }
  .keyboard-input {
    font-weight: 600;
    background-color: var(--color-secondary-bg);
    padding: 8px 12px 8px 12px;
    border-radius: 0.5rem;
    min-width: 146px;
    min-height: 34px;
    box-sizing: border-box;
    &.active {
      color: var(--color-primary);
      background-color: var(--color-primary-bg);
    }
  }
  .restore-default-shortcut {
    margin-top: 12px;
  }
  &.global-disabled {
    .row .col:last-child {
      opacity: 0.48;
    }
    .row.row-head .col:last-child {
      opacity: 1;
    }
  }
  &:focus {
    outline: none;
  }
}

.footer {
  text-align: center;
  margin-top: 6rem;
  color: var(--color-text);
  font-weight: 600;
  .author {
    font-size: 0.9rem;
  }
  .version {
    font-size: 0.88rem;
    opacity: 0.58;
    margin-top: -10px;
  }
}

.beforeAnimation {
  -webkit-transition: 0.2s cubic-bezier(0.24, 0, 0.5, 1);
  transition: 0.2s cubic-bezier(0.24, 0, 0.5, 1);
}
.afterAnimation {
  box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.1), 0 4px 0px 0 hsla(0, 0%, 0%, 0.04),
    0 4px 9px hsla(0, 0%, 0%, 0.13), 0 3px 3px hsla(0, 0%, 0%, 0.05);
  -webkit-transition: 0.35s cubic-bezier(0.54, 1.6, 0.5, 1);
  transition: 0.35s cubic-bezier(0.54, 1.6, 0.5, 1);
}
.toggle {
  margin: auto;
}
.toggle input {
  opacity: 0;
  position: absolute;
}
.toggle input + label {
  position: relative;
  display: inline-block;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-transition: 0.4s ease;
  transition: 0.4s ease;
  height: 32px;
  width: 52px;
  background: var(--color-secondary-bg);
  border-radius: 8px;
}
.toggle input + label:before {
  content: '';
  position: absolute;
  display: block;
  -webkit-transition: 0.2s cubic-bezier(0.24, 0, 0.5, 1);
  transition: 0.2s cubic-bezier(0.24, 0, 0.5, 1);
  height: 32px;
  width: 52px;
  top: 0;
  left: 0;
  border-radius: 8px;
}
.toggle input + label:after {
  content: '';
  position: absolute;
  display: block;
  box-shadow: 0 0 0 1px hsla(0, 0%, 0%, 0.02), 0 4px 0px 0 hsla(0, 0%, 0%, 0.01),
    0 4px 9px hsla(0, 0%, 0%, 0.08), 0 3px 3px hsla(0, 0%, 0%, 0.03);
  -webkit-transition: 0.35s cubic-bezier(0.54, 1.6, 0.5, 1);
  transition: 0.35s cubic-bezier(0.54, 1.6, 0.5, 1);
  background: #fff;
  height: 20px;
  width: 20px;
  top: 6px;
  left: 6px;
  border-radius: 6px;
}
.toggle input:checked + label:before {
  background: var(--color-primary-gradient);
  -webkit-transition: width 0.2s cubic-bezier(0, 0, 0, 0.1);
  transition: width 0.2s cubic-bezier(0, 0, 0, 0.1);
}
.toggle input:checked + label:after {
  left: 26px;
}

/* ScreenCard / Item desktop layout. The original YPM rules stay in its branch. */
.salt-settings-page { width: 100%; min-width: 0; margin: 0 auto; color: var(--salt-text); font-size: var(--salt-text-main,14px); container-type:inline-size; }
/* The shell already owns ScreenCard's outer horizontal 16px inset. */
.salt-settings-page ::v-deep(.inset > .salt-ui-card) { width:100%; margin:8px 0; }
.salt-settings-page ::v-deep(.section-heading), .salt-settings-page ::v-deep(.section-footer) { padding-left:16px; padding-right:16px; }
.salt-settings-header { display: flex; align-items: center; flex-wrap:wrap; gap:12px; min-height: 56px; padding: 0; }
.salt-settings-header h1 { margin: 0; font-size: var(--salt-text-title,24px); font-weight: 500; }
.salt-settings-header .settings-back { margin-inline-start:-8px; }
.salt-settings-link { display:block; color:inherit; text-decoration:none; }
.salt-settings-link + .salt-settings-link { border-top:var(--salt-hairline,1px) solid var(--salt-stroke); }
.salt-settings-link:hover { background:var(--salt-hover-overlay); }
.salt-settings-link ::v-deep(.salt-settings-category) { min-height:68px; }
.salt-settings-link ::v-deep(.salt-settings-category > .trailing) { flex:0 1 38%; max-width:38%; }
.salt-settings-link ::v-deep(.salt-settings-category .title) { font-weight:500; }
.salt-settings-link ::v-deep(.salt-settings-category .description) { line-height:1.5; }
.salt-slider-value, .salt-quality-controls, .salt-source-actions { display: flex; align-items: center; justify-content: flex-end; gap: 12px; min-width: 0; }
.salt-quality-controls { flex-wrap: wrap; }
.salt-setting-slider { padding-bottom:var(--salt-sub-padding,12px); }
.salt-setting-slider > .salt-ui-slider { display:block;width:calc(100% - var(--salt-padding,16px) * 2);margin:0 var(--salt-padding,16px); }
.salt-setting-slider output { min-width:52px;color:var(--salt-subtext);font-size:12px;white-space:nowrap;font-variant-numeric:tabular-nums; }
.salt-source-result { display: block; margin: 4px 0 0; color: var(--salt-subtext); font-size: 12px; line-height: 1.5; overflow-wrap: anywhere; }
.salt-settings-page kbd { font: inherit; font-size: 12px; color: var(--salt-subtext); white-space: nowrap; }
.salt-settings-footer { padding: 28px 16px 24px; color: var(--salt-subtext); font-size: 12px; line-height: 1.6; }
.salt-settings-footer p { margin: 4px 0; }
.salt-settings-footer a { color: inherit; text-decoration: none; }
.salt-settings-footer a:hover { color: var(--salt-accent); }
.salt-dav-connections { padding: 0 16px; }
.salt-dav-connections ::v-deep(h3) { display: none; }
.salt-dav-connections ::v-deep(.webdav-manager) { color: var(--salt-text); }
.salt-dav-connections ::v-deep(.section-note) { margin: 12px 0; color: var(--salt-subtext); font-size: 12px; opacity: 1; }
.salt-dav-connections ::v-deep(.item) { margin: 0; min-height: 32px; padding: 12px 0; gap: 12px; }
.salt-dav-connections ::v-deep(.title) { font-size: 14px; font-weight: 400; }
.salt-dav-connections ::v-deep(.description) { margin-top: 2px; color: var(--salt-subtext); font-size: 12px; opacity: 1; }
.salt-dav-connections ::v-deep(.danger-button), .salt-dav-connections ::v-deep(.dav-status.error) { color: var(--salt-error); }
.salt-dav-connections ::v-deep(.dav-status.success), .salt-dav-connections ::v-deep(.dav-status.working) { color: var(--salt-accent); }
.salt-dav-connections ::v-deep(.dav-editor) { padding: 16px; margin: 8px 0; border: var(--salt-hairline,1px) solid var(--salt-stroke); border-radius: 6px; background: var(--salt-sub-background); }
.salt-dav-connections ::v-deep(.dav-fields input) { min-height: 32px; border: 1px solid var(--salt-stroke); border-radius: 4px; padding: 6px 8px; background: var(--salt-popup); color: var(--salt-text); font-size: 14px; }
.salt-dav-connections ::v-deep(.dav-fields input:focus) { border-bottom-color: var(--salt-accent); }
/* Item descriptions remain beside their control; only compound fields need a second row. */
@mixin narrow-salt-settings-rows {
  ::v-deep(.salt-setting-row) { flex-wrap:nowrap; align-items:center; }
  ::v-deep(.salt-setting-row > .item-copy) { flex:1 1 0; min-width:0; }
  ::v-deep(.salt-setting-row > .trailing) { flex:0 0 auto; max-width:56%; margin:0 0 0 12px; justify-content:flex-end; text-align:end; }
  ::v-deep(.salt-setting-row > .trailing > .salt-ui-select) { max-width:100%; }
  ::v-deep(.salt-setting-toggle > .trailing) { flex:0 0 46px; width:46px; max-width:none; }
  .salt-settings-link ::v-deep(.salt-settings-category > .trailing) { flex:0 1 38%; max-width:38%; }
  .salt-slider-value { flex-wrap:nowrap; justify-content:flex-end; gap:8px; }
  ::v-deep(.salt-setting-quality) { flex-wrap:wrap; }
  ::v-deep(.salt-setting-quality > .item-copy) { flex:1 0 100%; padding-bottom:4px; }
  ::v-deep(.salt-setting-quality > .trailing) { width:100%; max-width:none; margin:0 0 12px; }
  .salt-quality-controls { width:100%; gap:8px; justify-content:flex-start; }
  .salt-quality-controls > .salt-ui-select { flex:1 1 110px; }
  ::v-deep(.salt-setting-source) { display:grid; grid-template-columns:minmax(0,1fr) 46px; column-gap:12px; }
  ::v-deep(.salt-setting-source > .item-copy) { grid-column:1; grid-row:1; }
  ::v-deep(.salt-setting-source > .trailing), .salt-source-actions { display:contents; }
  ::v-deep(.salt-setting-source .salt-ui-switch) { grid-column:2; grid-row:1; align-self:center; }
  ::v-deep(.salt-setting-source .salt-ui-button) { grid-column:1; grid-row:2; justify-self:start; margin-bottom:12px; }
  .salt-dav-connections ::v-deep(.credential-replace) { flex-direction:row; flex-wrap:nowrap; align-items:center; gap:12px; }
  .salt-dav-connections ::v-deep(.credential-replace > .item-copy) { flex:1 1 0; width:auto; }
  .salt-dav-connections ::v-deep(.credential-replace > .trailing) { flex:0 0 46px; width:46px; max-width:none; margin:0; }
  .salt-dav-connections ::v-deep(.saved-connection) { flex-direction:column; align-items:stretch; }
  .salt-dav-connections ::v-deep(.saved-connection > .trailing) { width:100%; max-width:none; margin-inline-start:0; }
  .salt-dav-connections ::v-deep(.source-actions) { max-width:100%; }
}
@media (max-width:650px) { .salt-settings-page { @include narrow-salt-settings-rows; } }
@container (max-width:620px) { .salt-settings-page { @include narrow-salt-settings-rows; } }
#app.salt-mobile-layout .salt-settings-page { @include narrow-salt-settings-rows; }
#app.salt-mobile-layout .salt-settings-page ::v-deep(.salt-ui-button),#app.salt-mobile-layout .salt-settings-page ::v-deep(.select-trigger) { min-height:44px; --button-height:44px; }
#app.salt-mobile-layout .salt-settings-page ::v-deep(.salt-ui-item.interactive) { min-height:44px; }
#app.salt-mobile-layout .salt-settings-page ::v-deep(.salt-ui-switch) { min-height:44px; height:44px; }
#app.salt-mobile-layout .salt-settings-page ::v-deep(.salt-ui-slider) { height:44px; }
#app.salt-mobile-layout .salt-settings-page ::v-deep(.slider-input) { height:44px; }
#app.salt-mobile-layout .salt-settings-page ::v-deep(.slider-rail) { inset-block-start:10.5px; }
#app.salt-mobile-layout .salt-dav-connections ::v-deep(.salt-dav-heading > .item-copy) { display:none; }
#app.salt-mobile-layout .salt-dav-connections ::v-deep(.salt-dav-heading > .trailing) { max-width:none; margin:0; }
</style>
